import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

// Ported from V1 `~/models/entities/components/views/DateController` (pure HTML5 canvas).

interface MapDateControllerProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
}

const HEIGHT = 48;
const HANDLE_WIDTH = 8;
const PADDING_X = 24;
const TICK_HEIGHT = 8;
const FONT = "11px system-ui, sans-serif";
const COLORS = {
  axis: "#9e9e9e",
  tick: "#bdbdbd",
  label: "#616161",
  range: "rgba(66,66,66,0.18)",
  handle: "#424242",
  handleHover: "#212121",
} as const;

type DragMode = "left" | "right" | "center" | null;

const yearToX = (year: number, min: number, max: number, width: number): number => {
  const drawWidth = width - PADDING_X * 2;
  if (max === min) return PADDING_X + drawWidth / 2;
  return PADDING_X + ((year - min) / (max - min)) * drawWidth;
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const pickTickInterval = (range: number, maxTicks: number): number => {
  const candidates = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000];
  for (const c of candidates) {
    if (range / c <= maxTicks) return c;
  }
  return Math.ceil(range / maxTicks);
};

export function MapDateController({ min, max, value, onChange }: MapDateControllerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const dragRef = useRef<{ mode: DragMode; startX: number; startValue: [number, number] } | null>(null);
  const [hoveredHandle, setHoveredHandle] = useState<DragMode>(null);
  const [isDragging, setIsDragging] = useState<DragMode>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    let rafId: number | null = null;
    const observer = new ResizeObserver((entries) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        for (const entry of entries) {
          const nextWidth = entry.contentRect.width;
          setWidth((prev) => (prev !== nextWidth ? nextWidth : prev));
        }
      });
    });
    observer.observe(containerRef.current);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = HEIGHT * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, HEIGHT);

    const axisY = HEIGHT - 14;

    ctx.strokeStyle = COLORS.axis;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PADDING_X, axisY);
    ctx.lineTo(width - PADDING_X, axisY);
    ctx.stroke();

    const range = max - min;
    const maxTicks = Math.floor((width - PADDING_X * 2) / 60);
    const interval = pickTickInterval(range, Math.max(maxTicks, 2));
    const firstTick = Math.ceil(min / interval) * interval;

    ctx.font = FONT;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    for (let year = firstTick; year <= max; year += interval) {
      const x = yearToX(year, min, max, width);
      ctx.strokeStyle = COLORS.tick;
      ctx.beginPath();
      ctx.moveTo(x, axisY - TICK_HEIGHT);
      ctx.lineTo(x, axisY);
      ctx.stroke();
      ctx.fillStyle = COLORS.label;
      ctx.fillText(String(year), x, axisY + 2);
    }

    const x0 = yearToX(value[0], min, max, width);
    const x1 = yearToX(value[1], min, max, width);
    ctx.fillStyle = COLORS.range;
    ctx.fillRect(x0, 4, x1 - x0, axisY - 4);

    const drawHandle = (x: number, hovered: boolean) => {
      ctx.fillStyle = hovered ? COLORS.handleHover : COLORS.handle;
      const hw = HANDLE_WIDTH / 2;
      ctx.beginPath();
      ctx.roundRect(x - hw, 2, HANDLE_WIDTH, axisY, 3);
      ctx.fill();
    };

    drawHandle(x0, hoveredHandle === "left");
    drawHandle(x1, hoveredHandle === "right");
  }, [width, min, max, value, hoveredHandle]);

  useEffect(() => {
    draw();
  }, [draw]);

  const getHitZone = useCallback(
    (clientX: number): DragMode => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const x0 = yearToX(value[0], min, max, width);
      const x1 = yearToX(value[1], min, max, width);
      const tolerance = HANDLE_WIDTH + 4;
      if (Math.abs(x - x0) <= tolerance) return "left";
      if (Math.abs(x - x1) <= tolerance) return "right";
      if (x > x0 + tolerance && x < x1 - tolerance) return "center";
      return null;
    },
    [value, min, max, width],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const mode = getHitZone(e.clientX);
      if (!mode) return;
      e.preventDefault();
      dragRef.current = { mode, startX: e.clientX, startValue: [...value] as [number, number] };
      setIsDragging(mode);
    },
    [getHitZone, value],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) {
        setHoveredHandle(getHitZone(e.clientX));
        return;
      }
      const deltaX = e.clientX - drag.startX;
      const drawWidth = width - PADDING_X * 2;
      const deltaYears = Math.round((deltaX / drawWidth) * (max - min));

      if (drag.mode === "left") {
        onChange([clamp(drag.startValue[0] + deltaYears, min, drag.startValue[1]), value[1]]);
      } else if (drag.mode === "right") {
        onChange([value[0], clamp(drag.startValue[1] + deltaYears, drag.startValue[0], max)]);
      } else if (drag.mode === "center") {
        const span = drag.startValue[1] - drag.startValue[0];
        let newStart = drag.startValue[0] + deltaYears;
        let newEnd = drag.startValue[1] + deltaYears;
        if (newStart < min) {
          newStart = min;
          newEnd = min + span;
        }
        if (newEnd > max) {
          newEnd = max;
          newStart = max - span;
        }
        onChange([newStart, newEnd]);
      }
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      setIsDragging(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [getHitZone, min, max, width, onChange, value]);

  // React 19 registers `wheel` as a passive root listener, so `onWheel` prop's preventDefault is ignored.
  // Attach natively with { passive: false } instead (same pattern as Timeline.tsx).
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      const drawWidth = width - PADDING_X * 2;
      const deltaYears =
        Math.round((e.deltaX / drawWidth) * (max - min)) ||
        Math.sign(e.deltaY) * Math.max(1, Math.round((max - min) / 50));
      const span = value[1] - value[0];
      let newStart = value[0] + deltaYears;
      let newEnd = value[1] + deltaYears;
      if (newStart < min) {
        newStart = min;
        newEnd = min + span;
      }
      if (newEnd > max) {
        newEnd = max;
        newStart = max - span;
      }
      onChange([newStart, newEnd]);
    },
    [min, max, value, width, onChange],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const cursor = (() => {
    if (isDragging === "center") return "grabbing";
    if (isDragging === "left" || isDragging === "right") return "col-resize";
    if (hoveredHandle === "left" || hoveredHandle === "right") return "col-resize";
    if (hoveredHandle === "center") return "grab";
    return "default";
  })();

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: HEIGHT }}>
      <canvas
        ref={canvasRef}
        className="block w-full"
        style={{ height: HEIGHT, cursor }}
        onMouseDown={handleMouseDown}
      />
    </div>
  );
}
